import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faFile, faHome } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import PageContext from '@contexts/pageContext'

import useAgencyProviderPackages from '@hooks/useAgencyProviderPackages'
import useAgencySelector from '@hooks/useAgencySelector'
import { generateUrls } from '@hooks/useProviderPackage'

import ListBlankState from '@components/ListBlankState'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import PackageListItem from '../PackageListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'package_title',
      label: 'Filter by Title',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      key: 'provider_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  useLocation()

  const {
    hidden = 'false',
  } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden']) as { hidden: 'true' | 'false' }

  const { selectedAgencyId } = useAgencySelector()

  const pageContext = useContext(PageContext)

  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const agencyProviderPackagesPayload = useAgencyProviderPackages({
    filters: {
      ...pageFilters,
      agency_id: selectedAgencyId,
      hidden,
    },
    performHttpRequests: !!selectedAgencyId,
    requestOptions: {
      include_agency_provider_package_provider_package: true,
      include_provider_package_provider: true,
    },
    sortKey: 'provider_package_id',
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyProviderPackages,
    filteredAgencyProviderPackagesCount,
    hasFilteredAgencyProviderPackages,
    loading,
  } = agencyProviderPackagesPayload

  const { hiddenProviderPackagesIndexUrl, providerPackagesIndexUrl } = generateUrls({}, {
    prefixRoute: `agencies/${selectedAgencyId}/manage`,
  })

  return (
    <>
      <Helmet>
        <title>Packages | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey={hidden}
        nestedNavigation
        tabBarItems={[
          {
            href: providerPackagesIndexUrl,
            icon: faHome,
            key: 'false',
            title: 'Current',
          },
          {
            href: hiddenProviderPackagesIndexUrl,
            icon: faFile,
            key: 'true',
            title: 'Hidden',
          },
        ]}
        title="Packages"
      />
      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.providerPackages.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredAgencyProviderPackages && <ListBlankState />}

            {!loading && hasFilteredAgencyProviderPackages && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Packages">
                <Box flexDirection="column">
                  {filteredAgencyProviderPackages.map(agencyProviderPackage => (
                    <PackageListItem agencyProviderPackage={agencyProviderPackage} key={agencyProviderPackage.id} />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredAgencyProviderPackagesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.providerPackages.sidebar" />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}
export default PageContent
